import React, { useState, useCallback, useEffect } from 'react';
import { DocumentIcon, Icon, FileDropArea, openErrorNotification } from 'hyarchis-global';
import { DocumentGroup } from 'types/documentGroup';
import { ProcessingJob } from 'types/proccesingJobs';
import { JobOutput, ExtendedFile } from 'types';
import poll from 'services/poller';
import { Tooltip, message, Button } from 'antd';
import {
  RedoOutlined,
  CloseOutlined,
  CaretRightOutlined,
  FileZipOutlined,
  FileSearchOutlined,
  LoadingOutlined,
  EditOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { DossierIcon } from 'hyarchis-global/components/icon';
import { AxiosError } from 'axios';
import { Workflow } from 'types/workflow';
import FileService from 'services/file-service';
import ProcessingJobRepository from 'services/api-repositories/processingJobs/ProcessingJobRepository';
import DocumentGroupRepository from 'services/api-repositories/documentsGroups/DocumentGroupRepository';
import styled from 'styled-components';
import downloadFile from '../processing-jobs/downloadFile';
import DocumentUploadForm from '../../upload/DocumentUploadForm';
import JobStatus from '../processing-jobs/JobStatus';
import Styled from '../listItemHead.styled';
import GroupListMatchButton from './GroupListMatchButton';
import DefinedInputSettingsDrawer from '../drawers/DefinedInputSettingsDrawer';

const {
  LeftColumn,
  MiddleColumn,
  RightColumn,
  GroupWrapper,
  HeadingWrapper,
  HeadTitleWrapper,
  DownloadButton,
  CollapseTogglerWrapper,
  ResultIconsWrapper,
  JobTitle,
  JobValue,
  HeadTitle,
  SubTitleWrapper,
  ActionIconsWrapper,
  FileDropAreaWrapper,
  ResultTitleWrapper,
  StyledStatusIcon,
  FlexColumn,
  StyledOutputTitle,
  CenteredSpinner,
} = Styled;

const StyledJobStatus = styled(JobStatus)`
  display: flex;
  align-items: center;
`;

interface OwnProps {
  documentGroup: DocumentGroup;
  collapsed: boolean;
  onCollapseClick: () => void;
  matchCollapsed: boolean;
  onMatchCollapseClick: () => void;
  onRemove: (groupId: string) => void;
  onRefresh: (group: DocumentGroup) => void;
  onProcessGroup: (group: DocumentGroup, source: string, documentIds: string[]) => void;
  setOutput: (document: JobOutput) => void;
}

const GroupListItemHead = ({
  documentGroup: documentsGroup,
  collapsed,
  onCollapseClick,
  matchCollapsed,
  onMatchCollapseClick,
  onRemove,
  onRefresh,
  onProcessGroup,
  setOutput,
}: OwnProps) => {
  const [openDefinedSettings, setDefinedSettingsOpen] = useState(false);
  const [documentGroup, setDocumentGroup] = useState<DocumentGroup>(documentsGroup);

  const job = documentsGroup.processingJob;
  const jobMatches = job?.output.crossMatchResult;
  const isError = !!job && job.status !== 'succeeded';
  const isLoading = job?.status === 'inProgress';

  const documentMergeLabel = job?.output.mergedFileId
    ? `Documents merged`
    : 'Documents merge was not performed';

  const matchResultsExists = job?.output.crossMatchResult !== undefined;
  const documentsMatchLabel = matchResultsExists
    ? `Documents matching performed`
    : 'Documents matching was not performed';
  const copyGroupId = () => {
    if (documentsGroup) {
      navigator.clipboard.writeText(documentsGroup?.id);
      message.success('Group Id copied to clipboard.', 1);
    }
  };

  const copyWorkflowId = () => {
    if (documentsGroup && documentsGroup?.documentWorkflow?.id) {
      navigator.clipboard.writeText(documentsGroup.documentWorkflow.id);
      message.success('Workflow Id copied to clipboard.', 1);
    }
  };

  const copyGroupWorkflowId = () => {
    if (documentsGroup && documentsGroup?.groupWorkflow?.id) {
      navigator.clipboard.writeText(documentsGroup.groupWorkflow.id);
      message.success('Group workflow Id copied to clipboard.', 1);
    }
  };

  const copyProcessingJobId = () => {
    if (documentsGroup && documentsGroup.processingJob) {
      navigator.clipboard.writeText(documentsGroup.processingJob.processingJobId);
      message.success('Group processing job Id copied to clipboard.', 1);
    }
  };

  const getWorkflowValue = (workflow?: Workflow): string =>
    workflow?.name ?? workflow?.id ?? 'Not set';

  const handleAddFiles = async (newFiles: File[]) => {
    const parsedFiles = await FileService.parseMultipleFiles(newFiles);

    for (const parsedFile of parsedFiles) {
      DocumentGroupRepository.createDocumentForDocumentGroup(
        parsedFile.originalFile,
        documentsGroup.id,
      ).then(
        async (response) => {
          const jobOutput: JobOutput = {
            file: parsedFile,
            documentsGroupId: documentsGroup.id,
            skipProcessing: documentsGroup.documentWorkflow?.id === undefined,
            documentId: response.documentId,
            processingJobId: response.processingJobId,
          };
          setOutput(jobOutput);
          if (response.processingJobId) {
            await processJob(
              response.processingJobId,
              parsedFile,
              documentsGroup.id,
              response.documentId,
            );
          }
        },
        (error) => {
          openErrorNotification((error as AxiosError).message);
        },
      );
    }
  };

  const processJob = async (
    processingJobId: string,
    file: ExtendedFile,
    groupId: string,
    documentId: string,
  ) => {
    let finishedJob: ProcessingJob;
    try {
      finishedJob = await poll<ProcessingJob>(
        () => ProcessingJobRepository.get(processingJobId),
        (fetchedJob) => fetchedJob.status === 'inProgress',
      );

      const jobOutput: JobOutput = {
        file,
        job: finishedJob,
        documentsGroupId: groupId,
        skipProcessing: false,
        documentId,
      };

      setOutput(jobOutput);
    } catch (error) {
      openErrorNotification((error as Error).message);
    }
  };

  const handleClose = () => {
    setDefinedSettingsOpen(false);
  };

  const handleDocumentValuesChange = useCallback((values) => {
    setDocumentGroup(values);
  }, []);

  useEffect(() => {
    setDocumentGroup(documentGroup);
  }, [documentGroup]);

  return (
    <GroupWrapper>
      <LeftColumn>
        <HeadingWrapper>
          {documentsGroup && (
            <HeadTitleWrapper>
              <DossierIcon size={20} />
              <Tooltip title={documentsGroup.groupName}>
                <HeadTitle>{documentsGroup.groupName}</HeadTitle>
              </Tooltip>
            </HeadTitleWrapper>
          )}
          <SubTitleWrapper>
            <JobTitle>GROUP ID:</JobTitle>
            <Tooltip title='Click to copy'>
              <JobValue onClick={copyGroupId}>{documentsGroup.id}</JobValue>
            </Tooltip>
          </SubTitleWrapper>
          <SubTitleWrapper>
            <JobTitle>JOB WORKFLOW:</JobTitle>
            <Tooltip title='Click to copy Id'>
              <JobValue onClick={copyWorkflowId}>
                {getWorkflowValue(documentsGroup.documentWorkflow)}
              </JobValue>
            </Tooltip>
          </SubTitleWrapper>
          <SubTitleWrapper>
            <JobTitle>GROUP WORKFLOW:</JobTitle>
            <Tooltip title='Click to copy Id'>
              <JobValue onClick={copyGroupWorkflowId}>
                {getWorkflowValue(documentsGroup.groupWorkflow)}
              </JobValue>
            </Tooltip>
          </SubTitleWrapper>
          {documentsGroup?.processingJob !== undefined && (
            <SubTitleWrapper>
              <JobTitle>PROCESSING JOB ID:</JobTitle>
              <Tooltip title='Click to copy Id'>
                <JobValue onClick={copyProcessingJobId}>
                  {documentsGroup.processingJob.processingJobId ?? ''}
                </JobValue>
              </Tooltip>
            </SubTitleWrapper>
          )}
        </HeadingWrapper>
      </LeftColumn>
      <MiddleColumn>
        {isLoading && (
          <CenteredSpinner indicator={<LoadingOutlined style={{ fontSize: 35 }} spin />} />
        )}
        {isError && (
          <StyledJobStatus
            status={job?.status || ''}
            message={job?.error?.errorMessage || ''}
            timeout={false}
          />
        )}
        <FlexColumn>
          {!isError && job && job.output.mergedFileId && (
            <ResultTitleWrapper>
              <DocumentIcon sizeType='large' type='pdf' size={20} />
              <StyledOutputTitle disabled={false}>Merged pdf file</StyledOutputTitle>
              <Tooltip title='Download as PDF'>
                <DownloadButton
                  onClick={downloadFile(
                    job.output.mergedFileId,
                    `${job.createdAt}--${job.processingJobId}.pdf`,
                  )}
                  size='small'
                  shape='circle'
                  icon={<FilePdfOutlined />}
                />
              </Tooltip>
            </ResultTitleWrapper>
          )}
          {!isError && job && (
            <ResultIconsWrapper>
              <StyledStatusIcon
                icon={FileZipOutlined}
                title={documentMergeLabel}
                enabled={job?.output.mergedFileId !== undefined}
              />
              <StyledStatusIcon
                icon={FileSearchOutlined}
                title={documentsMatchLabel}
                enabled={matchResultsExists}
              />
              {matchResultsExists && jobMatches && (
                <ResultIconsWrapper>
                  <CollapseTogglerWrapper>
                    <Button
                      icon={
                        <Icon
                          icon={!matchCollapsed ? 'chevronUp' : 'chevronDown'}
                          type='regular'
                          size={24}
                        />
                      }
                      size='large'
                      type='text'
                      onClick={(e) => onMatchCollapseClick()}
                    />
                  </CollapseTogglerWrapper>
                  <GroupListMatchButton
                    groupName={documentsGroup.groupName}
                    jobMatches={jobMatches}
                  />
                </ResultIconsWrapper>
              )}
            </ResultIconsWrapper>
          )}
        </FlexColumn>
      </MiddleColumn>
      <RightColumn>
        <FileDropAreaWrapper>
          <FileDropArea addFiles={handleAddFiles} draggingStyle={{ opacity: 1 }}>
            <DocumentUploadForm allowMultiple addFiles={handleAddFiles} />
          </FileDropArea>
        </FileDropAreaWrapper>
        <DefinedInputSettingsDrawer
          setOpenDrawer={openDefinedSettings}
          onClose={handleClose}
          documentGroup={documentGroup}
          onDocumentGroupChange={handleDocumentValuesChange}
        />
        <CollapseTogglerWrapper>
          <Button
            icon={<Icon icon={!collapsed ? 'chevronUp' : 'chevronDown'} type='regular' size={24} />}
            size='large'
            type='text'
            onClick={(e) => onCollapseClick()}
          />
        </CollapseTogglerWrapper>
      </RightColumn>
      <ActionIconsWrapper>
        <Button size='small' type='text' onClick={() => setDefinedSettingsOpen(true)}>
          <EditOutlined />
        </Button>
        {documentGroup.groupWorkflow?.id && (
          <Button
            size='small'
            type='text'
            onClick={(e) =>
              onProcessGroup(
                documentGroup,
                documentGroup.externalDataSource,
                documentGroup.documentIdsToExtract,
              )
            }
          >
            <CaretRightOutlined />
          </Button>
        )}
        <Button size='small' type='text' onClick={(e) => onRefresh(documentGroup)}>
          <RedoOutlined />
        </Button>
        <Button size='small' type='text' onClick={(e) => onRemove(documentGroup.id)}>
          <CloseOutlined />
        </Button>
      </ActionIconsWrapper>
    </GroupWrapper>
  );
};

export default GroupListItemHead;
